// Stateless helpers for POTA hunter stats. The reactive state, watchers, and
// orchestration live in HunterStatsStore (which imports these). Keeping these
// side-effect-free of the store avoids an import cycle; the pure aggregation lives in
// hunterStatsAggregate so it can be tested without instantiating PouchDB.
import PouchDB from '@/repos/PouchDB';
import QsoService from '@/services/QsoService';
import HunterParkPouchRepo from '@/repos/HunterParkPouchRepo';
import { accumulateQso } from '@/lib/hunterStatsAggregate';
import { QSO_ID_PREFIX } from '@/constants';
import { potaParksLogger } from '@/utils/loggers';
import type { LocalParkStat, HunterParkCsvRow } from '@/types';

export { statsFromQsos, mergeStats } from '@/lib/hunterStatsAggregate';

// Notify whenever a QSO doc changes in ANY logbook (live sync, bulk move/edit, ADIF
// import). Lets the store keep cross-logbook base stats fresh without polling.
// `since: 'now'` ignores the initial backlog. A live feed can drop (e.g. a transient
// IndexedDB error), which would otherwise silently stall stats, so the `error` event is
// handled by restarting the feed. Returns a handle the caller MUST cancel on teardown.
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30000;

export function subscribeToQsoChanges(
  onChange: (changedId: string) => void
): { cancel: () => void } {
  let cancelled = false;
  let feed: ReturnType<typeof PouchDB.changes> | null = null;
  // Resume point: start after the current state, but on restart resume from the last change
  // we actually saw so updates during downtime are replayed (each still triggers onChange)
  // rather than skipped by a fresh `since: 'now'`.
  let since: 'now' | string | number = 'now';
  let retryDelay = RETRY_BASE_MS;

  const start = () => {
    if (cancelled) return;
    feed = PouchDB.changes({ since, live: true })
      .on('change', (change: any) => {
        if (change?.seq !== undefined) since = change.seq;
        retryDelay = RETRY_BASE_MS; // a healthy change resets the backoff
        if (typeof change?.id === 'string' && change.id.startsWith(`${QSO_ID_PREFIX}:`)) {
          onChange(change.id);
        }
      })
      .on('error', (error: any) => {
        potaParksLogger('Hunter QSO changes feed error; restarting', error);
        try {
          feed?.cancel();
        } catch {
          /* already gone */
        }
        if (cancelled) return;
        const delay = retryDelay;
        retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS); // capped exponential backoff
        setTimeout(start, delay);
      });
  };

  start();

  return {
    cancel: () => {
      cancelled = true;
      try {
        feed?.cancel();
      } catch {
        /* already gone */
      }
    },
  };
}

// Per-park stats across all logbooks EXCEPT `excludeLogbookId` (the open logbook,
// whose stats are overlaid live from loggerStore.qsoList to avoid double-counting).
// Chunked so a large account doesn't block the main thread.
export async function computeBaseStats(
  excludeLogbookId?: string | null
): Promise<Map<string, LocalParkStat>> {
  const qsos = await QsoService.findAll();
  const map = new Map<string, LocalParkStat>();
  // QSO ids are `QSO:{logbookId}:{uuid}`; logbookId is `LOGBOOK:{uuid}`.
  const excludePrefix = excludeLogbookId ? `QSO:${excludeLogbookId}:` : null;
  for (let i = 0; i < qsos.length; i++) {
    const qso = qsos[i];
    if (excludePrefix && qso._id?.startsWith(excludePrefix)) continue;
    accumulateQso(map, qso);
    if ((i & 8191) === 8191) await new Promise((resolve) => setTimeout(resolve, 0));
  }
  return map;
}

export async function loadCsvRows(): Promise<HunterParkCsvRow[]> {
  return HunterParkPouchRepo.findAll();
}

export async function persistCsvRows(rows: HunterParkCsvRow[]): Promise<void> {
  await HunterParkPouchRepo.replaceAll(rows);
}

export async function clearCsv(): Promise<void> {
  await HunterParkPouchRepo.clearAll();
}

export async function getCsvMetadata() {
  return HunterParkPouchRepo.getMetadata();
}

export default {
  computeBaseStats,
  loadCsvRows,
  persistCsvRows,
  clearCsv,
  getCsvMetadata,
  subscribeToQsoChanges,
};
